/**
 * models.ts — Character meshes built from primitives, with procedural animation.
 *
 * This is the 3D counterpart of the old 2D placeholder-art module: simple boxes and
 * spheres assembled into a walker, a dog, small critters, and cars. Because they're
 * real 3D rigs (limbs are separate meshes parented to a group), the walk cycle is
 * animated by *rotating limbs* rather than by swapping spritesheet frames.
 *
 * SWAPPING IN REAL ART: each `create*` function returns a `CharacterRig` — a group
 * plus the limb references the animator drives. To use real models, load a glTF and
 * return its root as `group` with the equivalent bones mapped into the same fields;
 * nothing outside this file changes. See README → "Swapping in real models".
 *
 * All models face **+z** at rest, matching `facingYaw()` in entities/locomotion.ts.
 */
import * as THREE from 'three';
import {
  PLAYER,
  DOG,
  HAZARD,
  VEHICLES,
  ANIM,
  CLAY,
  OBJECTIVE,
  EFFECTS,
  PARK_FURNITURE,
  YARDS,
} from '../config/constants';
import { CHARACTERS, FOLIAGE, PROPS } from '../config/palette';
import { arc01 } from '../utils/easing';
import { shadeHex } from '../utils/color';
import { clayBox, clayMaterial, clayPill, claySphere } from './clay';
import type { DogPose } from '../entities/dogPose';
import type { HazardType, VehicleKind } from '../types';

/** A model plus the parts the animator moves. Limb arrays may be empty. */
export interface CharacterRig {
  group: THREE.Group;
  /** Limbs swung fore/aft in antiphase pairs during a walk cycle. */
  legs: THREE.Object3D[];
  arms: THREE.Object3D[];
  /** Body node that bobs vertically; usually everything above the legs. */
  body?: THREE.Object3D;
  tail?: THREE.Object3D;
  /** Ear pivots, rotated to perk up or pin back with mood. */
  ears: THREE.Object3D[];
  /**
   * Wing pivots, rolled up and down by `animateFlap`. Only the pigeon has any.
   *
   * Deliberately *not* `arms`: `animateWalk` swings arms fore and aft in
   * antiphase, which is a person striding. A bird beats both wings together,
   * about a different axis, and only when it is in the air.
   */
  wings?: THREE.Object3D[];
  /** Resting height of `body` so bobbing can offset from it. */
  bodyBaseY: number;
}

/**
 * Every part of every character is rounded clay — see render/clay.ts for what
 * that means and why. `box` here is a *rounded* box; there are no hard edges on
 * any model in this file, and adding one would stick out badly.
 */
const box = clayBox;

/**
 * A boot or paw on the end of a limb, in ground-plane dimensions. `forward` is
 * how far the toe pokes out past the limb in +z.
 */
interface Foot {
  color: number;
  width: number;
  height: number;
  depth: number;
  forward?: number;
}

/**
 * A limb that rotates about its TOP end (a hip/shoulder pivot), not its centre.
 * The mesh is offset down inside a pivot group so rotating the group swings the
 * limb like a real leg.
 *
 * Limbs are rolled *sausages* rather than boxes: `thickness` is the diameter and
 * `len` the full length including the rounded caps. A capsule is the shape you
 * actually get when you roll clay between your palms, and it's the main thing
 * keeping the cast from reading as a stack of dice.
 *
 * Pass `foot` for a boot or a paw at the far end. It's parented inside the pivot,
 * so it swings with the limb and needs no animation of its own — and because the
 * foot is what actually meets the ground, a limb with one is the thing that gives
 * the character a contact shadow you can read.
 */
function pivotLimb(
  thickness: number,
  len: number,
  color: number,
  at: THREE.Vector3,
  foot?: Foot,
): THREE.Group {
  const pivot = new THREE.Group();
  pivot.position.copy(at);
  const r = thickness / 2;
  const mesh = clayPill(r, Math.max(len - thickness, 0.01), color);
  mesh.position.y = -len / 2; // hang from the pivot
  pivot.add(mesh);
  if (foot) {
    pivot.add(
      box(
        foot.width,
        foot.height,
        foot.depth,
        foot.color,
        0,
        -len + foot.height / 2,
        foot.forward ?? 0,
      ),
    );
  }
  return pivot;
}

// ---- player ------------------------------------------------------------------

/**
 * Hair, built as a heap of overlapping clay lumps on top of the head sphere.
 *
 * The lumps are the entire point. A single ball over the skull is a **beanie**,
 * and a rounded box is a **flat cap** — both were tried. What separates hair from
 * headwear at this size is three things, none of them detail you can see up close:
 *
 * 1. A **bumpy silhouette**. Hats are smooth; hair isn't.
 * 2. **Asymmetry** — the fringe is swept to one side and no two lumps match.
 *    Anything mirror-symmetric up here reads as manufactured.
 * 3. **No continuous edge round the head.** A hat ends in a band; hair comes down
 *    at the nape and in front of the ears, which is what the last two pieces do.
 *
 * Offsets are relative to the body group, matching the head sphere at y=17.
 */
function addHair(body: THREE.Group): void {
  const hair = PLAYER.HAIR_COLOR;
  const lump = (r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1) => {
    const mesh = claySphere(r, hair, x, y, z);
    mesh.scale.set(sx, sy, sz);
    body.add(mesh);
  };

  lump(7.5, 0, 19.2, -0.5); // the mass of it, sitting just proud of the skull
  // Lumps breaking the outline. Deliberately uneven in size and spacing.
  lump(3.6, -4.3, 22.1, -1.8);
  lump(3.9, 3.5, 22.5, -0.4);
  lump(3.3, 5.6, 20.2, -4.4);
  lump(3.1, -5.2, 19.8, -4.6);
  lump(3.8, 0.6, 21.2, -6.1);
  // Fringe, swept right — the asymmetry is what stops the head reading as a cap,
  // and from directly overhead it's what gives the head a front at all.
  lump(5.6, 1.7, 20.1, 3.2, 1.15, 0.5, 0.95);
  lump(3.4, -3.8, 19.6, 4.6, 1, 0.55, 0.9);
  // Nape and sideburns: hair below where a hat's band would stop.
  lump(4.2, 0, 15.4, -6.3, 1.25, 0.75, 0.8);
  lump(1.9, -6.3, 15.8, 1.4, 0.75, 1.25, 0.9);
  lump(1.9, 6.3, 15.8, 1.4, 0.75, 1.25, 0.9);
}

/**
 * The walker: red tee, jeans, boots, bare-headed. That's the whole outfit.
 *
 * It is deliberately the *simplest* thing on screen, arrived at by subtraction.
 * A peaked cap, a shoulder yoke, brass buttons, a belt and a scarf all went on
 * this model at some point, and every one of them made him read as a uniform —
 * a postman — because at 46 units tall a layer is just a stripe, and stripes on
 * a torso mean livery. Two ribbed bands and a short sleeve are the limit.
 *
 * What survives is chosen to be legible from a camera looking 58° down, where
 * all you see of a person is the top of their head and their shoulders: the red
 * carries the silhouette against paving, tarmac and grass alike; skin forearms
 * against red sleeves keep the arms off the torso; and the fringe in the hair
 * gives the head a front.
 */
export function createPlayer(): CharacterRig {
  const group = new THREE.Group();
  const legH = 16;
  const bodyBaseY = legH + 11;

  const body = new THREE.Group();
  body.position.y = bodyBaseY;
  // Torso is deliberately deep as well as wide (13.5 front-to-back, not 11): a
  // thin slab of clay looks like a biscuit from this camera. Same reasoning
  // sizes the limbs below — thin clay reads as wire, not as a rolled sausage.
  body.add(box(19, 21, 13.5, PLAYER.COLOR, 0, 0, 0)); // the tee
  // Ribbed collar and hem, in the deeper red. Two bands is the whole outfit —
  // a yoke, buttons, a belt or a scarf each add a layer, and layers on a torso
  // this size stop reading as clothes and start reading as a uniform.
  body.add(box(11, 2.4, 10.5, PLAYER.SHIRT_DEEP_COLOR, 0, 10.2, 0));
  body.add(box(19.4, 2.2, 13.9, PLAYER.SHIRT_DEEP_COLOR, 0, -9.6, 0));

  body.add(claySphere(7.5, PLAYER.SKIN_COLOR, 0, 17, 0)); // head
  body.add(claySphere(1.8, PLAYER.SKIN_COLOR, -7.2, 17.4, 0)); // ears
  body.add(claySphere(1.8, PLAYER.SKIN_COLOR, 7.2, 17.4, 0));
  body.add(claySphere(1.5, PLAYER.SKIN_COLOR, 0, 16.3, 7.4)); // nose
  body.add(claySphere(1.05, PLAYER.EYE_COLOR, -2.8, 17.8, 6.9)); // eyes
  body.add(claySphere(1.05, PLAYER.EYE_COLOR, 2.8, 17.8, 6.9));
  addHair(body);

  // Bare arms with a short sleeve over the top third. The sleeve is doing real
  // work as well as dressing him: skin against red is the tonal break that keeps
  // the arms from melting into the torso from directly above.
  const arms = [-11, 11].map((ax) => {
    const arm = pivotLimb(6, 17, PLAYER.SKIN_COLOR, new THREE.Vector3(ax, 8, 0));
    arm.add(box(7.2, 7, 7.2, PLAYER.COLOR, 0, -3, 0)); // sleeve
    arm.add(box(7.4, 1.4, 7.4, PLAYER.SHIRT_DEEP_COLOR, 0, -6.4, 0)); // its hem
    arm.add(claySphere(3.3, PLAYER.SKIN_COLOR, 0, -16.4, 0)); // fist
    body.add(arm);
    return arm;
  });

  // Legs in boots. The boot is the part that meets the pavement, so the walker
  // now plants a foot instead of tapering away into the ground.
  const legs = [-4.5, 4.5].map((lx) =>
    pivotLimb(7.5, legH, PLAYER.TROUSERS_COLOR, new THREE.Vector3(lx, legH, 0), {
      color: PLAYER.BOOT_COLOR,
      width: 7.6,
      height: 4.6,
      depth: 11,
      forward: 1.8,
    }),
  );
  legs.forEach((l) => group.add(l));
  group.add(body);

  return { group, legs, arms, body, ears: [], bodyBaseY };
}

/**
 * Knotted bag the player carries after cleaning up. Parented to the walker's
 * *arm* (see Player.setCarryingBag), so it hangs from the hand and swings with
 * the walk cycle — the offsets below are relative to the shoulder pivot.
 *
 * Modelled as a soft cinched clay bag:
 * - A bulging lower pouch (where the contents sit)
 * - A gathered cinched neck
 * - A central tied knot
 * - Two floppy tie handles/ears sticking out from the knot
 */
export function createBag(): THREE.Group {
  const bag = new THREE.Group();
  const color = OBJECTIVE.BAG_COLOR;

  // Bulging lower pouch: soft rounded clay body
  const pouch = clayBox(7.2, 8, 6.8, color, 0, -21.5, 1.5);
  bag.add(pouch);

  // Bulge near the bottom of the pouch to give it weight/form
  const bulge = claySphere(3.6, color, 0, -23.0, 1.5);
  bag.add(bulge);

  // Gathered neck transition above the pouch
  const neck = claySphere(2.3, color, 0, -18.6, 1.5);
  bag.add(neck);

  // Tied knot
  const knot = claySphere(2.1, color, 0, -17.0, 1.5);
  bag.add(knot);

  // Two floppy tie-handle loops branching out from the knot
  const earL = clayPill(0.9, 3.2, color);
  earL.position.set(-1.4, -15.4, 1.5);
  earL.rotation.z = 0.55;
  earL.rotation.x = -0.2;
  bag.add(earL);

  const earR = clayPill(0.9, 3.2, color);
  earR.position.set(1.4, -15.4, 1.5);
  earR.rotation.z = -0.55;
  earR.rotation.x = 0.2;
  bag.add(earR);

  return bag;
}

/**
 * Hand-sculpted clay poop emoji swirl.
 *
 * Built from three stacked clay tiers:
 * 1. Base ring: 4 overlapping clay spheres forming a wide Play-doh base coil.
 * 2. Middle ring: 3 overlapping clay spheres forming a tighter middle layer.
 * 3. Top tip: a small perked clay sphere/pill with a slight curl tilt.
 */
export function createPoop(): THREE.Group {
  const group = new THREE.Group();
  const color = EFFECTS.POOP_COLOR;

  // Base tier: 4 overlapping spheres forming a wide Play-doh coil base
  const baseR = 2.4;
  const baseOffset = 2.2;
  const baseHeights = [1.8, 1.8, 1.8, 1.8];
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    const x = Math.cos(angle) * baseOffset;
    const z = Math.sin(angle) * baseOffset;
    const s = claySphere(baseR, color, x, baseHeights[i], z);
    s.scale.set(1.1, 0.75, 1.1);
    group.add(s);
  }

  // Middle tier: 3 overlapping spheres forming a tighter middle layer
  const midR = 1.9;
  const midOffset = 1.2;
  for (let i = 0; i < 3; i++) {
    const angle = (i * 2 * Math.PI) / 3 + Math.PI / 6;
    const x = Math.cos(angle) * midOffset;
    const z = Math.sin(angle) * midOffset;
    const s = claySphere(midR, color, x, 3.4, z);
    s.scale.set(1.0, 0.75, 1.0);
    group.add(s);
  }

  // Top tip: perked swirl peak
  const tip = claySphere(1.4, color, 0.2, 4.8, -0.2);
  tip.scale.set(0.85, 1.1, 0.85);
  tip.rotation.z = -0.25;
  group.add(tip);

  return group;
}

// ---- dog ---------------------------------------------------------------------

/**
 * The dog: a scruffy brown terrier in a collar.
 *
 * Its body is three blocks rather than one — deep chest, narrower waist, higher
 * rump — because a single box is a loaf from any angle and especially from
 * overhead. The other half of the job is done with markings: a dark saddle down
 * the spine and a pale blaze over the skull, which are the only two things you
 * can reliably see when the dog is directly below the camera.
 *
 * Overall nose-to-tail reach is kept to the old model's — DOG.BODY_RADIUS is
 * sized off it for the walker↔dog separation, so a longer dog would start
 * holding the walker further away.
 */
export function createDog(): CharacterRig {
  const group = new THREE.Group();
  const legH = 9;
  const bodyBaseY = legH + 7;

  const body = new THREE.Group();
  body.position.y = bodyBaseY;

  // Torso in three parts, long along +z: chest, waist, rump. The waist is 1.5
  // units narrower than both ends, which is enough to give the dog a shape from
  // above without shortening it.
  body.add(box(15, 13, 11, DOG.COLOR, 0, 0.5, 5.5)); // chest
  body.add(box(13.5, 12, 9, DOG.COLOR, 0, 0, -1)); // waist
  body.add(box(15, 12.5, 9, DOG.COLOR, 0, 0.5, -7)); // rump
  // Saddle patch: a dark stripe along the spine, leaving body colour on the
  // flanks. The back is the biggest surface the camera sees, so it does more
  // for readability than anything else on the model. Its top is flush with the
  // chest — stand it any prouder and it stops being a marking and starts being
  // a lid, and level with it exactly and the two faces z-fight.
  body.add(box(12, 2, 17, DOG.DARK_COLOR, 0, 6.2, -1));
  body.add(box(7.5, 6, 4, DOG.PALE_COLOR, 0, -3.5, 9.5)); // pale chest

  // Neck and collar. The collar sits at LEASH.COLLAR_HEIGHT, so the lead now
  // visibly clips to something instead of ending in mid-fur.
  body.add(box(9.5, 9, 6, DOG.COLOR, 0, 3.5, 11.5));
  body.add(box(10.5, 2.8, 4.6, DOG.COLLAR_COLOR, 0, 2.5, 11.8));
  body.add(claySphere(1.3, DOG.COLLAR_TAG_COLOR, 0, 0.6, 13.6)); // name tag

  // The head is deliberately NARROWER than the torso and sits higher and further
  // forward than it strictly needs to. This camera looks almost straight down, so
  // a head flush with the shoulders merges into the torso and the dog becomes a
  // loaf — the shoulder step and the pale muzzle below are what read as a head.
  body.add(box(10.5, 10, 10, DOG.COLOR, 0, 6.5, 15.5));
  body.add(box(10, 2.2, 5, DOG.DARK_COLOR, 0, 10.5, 16.5)); // dark brow
  body.add(box(2.8, 1.6, 9, DOG.PALE_COLOR, 0, 11.2, 17.5)); // blaze up the skull
  body.add(claySphere(1.3, DOG.EYE_COLOR, -3.4, 9.4, 20));
  body.add(claySphere(1.3, DOG.EYE_COLOR, 3.4, 9.4, 20));
  body.add(box(6.5, 5.5, 8, DOG.PALE_COLOR, 0, 4.6, 21.5)); // muzzle
  body.add(box(5.5, 2.4, 6.5, DOG.DARK_COLOR, 0, 1.9, 21)); // jaw
  body.add(claySphere(2, DOG.NOSE_COLOR, 0, 5.3, 25));

  // Ears on pivots at their base, so they can perk up / pin back with mood.
  // Mounted on top of the head (y above the head's crown) so they break the
  // silhouette against the ground rather than disappearing into the skull. The
  // pale inner flap faces forward, which is what makes a pinned-back ear read as
  // pinned back rather than just shorter.
  const ears = [-4.2, 4.2].map((ex) => {
    const pivot = new THREE.Group();
    pivot.position.set(ex, 11, 14.5);
    pivot.add(box(4.5, 8, 3, DOG.DARK_COLOR, 0, 4, 0));
    pivot.add(box(2.2, 5, 1.6, DOG.PALE_COLOR, 0, 3.6, 1.4));
    body.add(pivot);
    return pivot;
  });

  // Limbs hang downward from their pivot, so rotating x toward π swings them up.
  // ~2.2 rad puts the tail up and back over the rump rather than drooping.
  const tail = pivotLimb(3.5, 11, DOG.DARK_COLOR, new THREE.Vector3(0, 6, -11));
  tail.add(claySphere(2, DOG.PALE_COLOR, 0, -10.4, 0)); // pale tip
  tail.rotation.x = 2.2;
  body.add(tail);

  // Four legs in pale socks: front pair then back pair. The socks matter more
  // than they sound — dark legs against a dark saddle lose the trot entirely
  // from overhead, and four pale paws put the gait back.
  const paw = { color: DOG.PALE_COLOR, width: 5.2, height: 3, depth: 6.2, forward: 1.2 };
  const legs = [
    pivotLimb(5, legH, DOG.DARK_COLOR, new THREE.Vector3(-5, legH, 7.5), paw),
    pivotLimb(5, legH, DOG.DARK_COLOR, new THREE.Vector3(5, legH, 7.5), paw),
    pivotLimb(5, legH, DOG.DARK_COLOR, new THREE.Vector3(-5, legH, -7.5), paw),
    pivotLimb(5, legH, DOG.DARK_COLOR, new THREE.Vector3(5, legH, -7.5), paw),
  ];
  legs.forEach((l) => group.add(l));
  group.add(body);

  return { group, legs, arms: [], body, tail, ears, bodyBaseY };
}

// ---- hazards -----------------------------------------------------------------

/**
 * A critter. Front is +z.
 *
 * The cat and the fox used to be **the same model in two colours** — one
 * rounded box, a smaller box for a head, two box ears (the fox's a bit taller),
 * a snout dot and a stick tail. At the size these run around at, that made them
 * a ginger bean and a red bean, and there was no angle from which you could
 * tell which was which. That matters more than it sounds: a cat sends the dog
 * into a hyper pull, and a fox tangles *your* leash. They're two different
 * threats wearing the same body.
 *
 * They're now built separately, around what actually distinguishes the two
 * animals **in plan**, which is nearly all this camera gives you:
 *
 * - **A cat is small, narrow and striped, with a thin tail carried up.** The
 *   tabby barring across its back is the cat's version of the dog's saddle
 *   patch — the back is the largest surface the camera ever sees, and it was
 *   blank. The upright, hooked tail is the other half.
 * - **A fox is longer, lower, and mostly tail.** The brush trails level behind
 *   it, nearly as long as its body, and it ends in a white tip — from directly
 *   above that pale flag is the single most legible thing on either animal. It
 *   also has a long pointed muzzle where the cat has a round face, and black
 *   stockings against the cat's cream paws.
 *
 * The squirrel followed, and its signature is the same kind of fact: a broad
 * bushy plume arched up and hooked forward over its back, rimmed cream. Three
 * animals, three tails, and you can name any of them from directly above
 * without seeing a face.
 *
 * The pigeon is the exception that proves the rule — a bird has no tail worth
 * the name from up here, so it is identified on **grey** and on the outline of
 * two wing panels instead. See `createPigeon`.
 */
export function createHazard(type: HazardType): CharacterRig {
  if (type === 'cat') return createCat();
  if (type === 'fox') return createFox();
  if (type === 'pigeon') return createPigeon();
  return createSquirrel();
}

/** Assemble a critter's rig from a body group and four legs at the corners. */
function critterRig(
  body: THREE.Group,
  bodyBaseY: number,
  legs: THREE.Group[],
  tail: THREE.Group,
  ears: THREE.Group[],
): CharacterRig {
  const group = new THREE.Group();
  body.position.y = bodyBaseY;
  legs.forEach((l) => group.add(l));
  group.add(body);
  return { group, legs, arms: [], body, tail, ears, bodyBaseY };
}

/**
 * A ginger tabby. Small, narrow, and carried high on its tail.
 *
 * Everything here is chosen for the overhead view. The **stripes** are the
 * headline: four dark bars across the spine, standing a fraction proud of the
 * back so they catch their own edge rather than reading as paint. The **tail**
 * is the other one — thin, held nearly upright and hooked at the tip, which is
 * a shape no other animal in the game makes and is visible even when the cat
 * itself is behind a bush.
 *
 * Note the body is deliberately *narrower* than the fox's and the head rounder.
 * A cat seen from above is a slim thing with a ball on the front; a fox is a
 * wedge. Getting that pair of silhouettes apart was the whole job.
 */
function createCat(): CharacterRig {
  const coat = HAZARD.COLOR.cat;
  const dark = HAZARD.DARK_COLOR.cat;
  const legH = 5.5;
  const body = new THREE.Group();

  // Torso in two parts, narrow and low. The shoulders are the widest point, so
  // the head in front of them still reads as a head.
  body.add(box(7, 6.5, 8.5, coat, 0, 0, 3));
  body.add(box(7.5, 6.5, 7, coat, 0, 0, -3.5));
  body.add(box(4.5, 3.5, 3.5, CHARACTERS.CAT_PALE, 0, -2.2, 7)); // bib

  // Tabby barring: four bars across the spine, and the cat's answer to the
  // dog's saddle patch — the back is the largest surface this camera ever sees
  // and it was blank.
  //
  // Narrower than the torso and barely proud of it. Wider, and they wrap the
  // rounded flanks and read as hoops around a barrel; prouder, and they stop
  // being markings and become a rack of ribs. Both were built before this.
  for (const z of [5, 1.8, -1.4, -4.6]) {
    body.add(box(6.5, 0.9, 1.6, CHARACTERS.CAT_STRIPE, 0, 2.95, z));
  }

  // A round skull, set forward and above the shoulders. Round is the point: it
  // is what the fox's wedge is measured against.
  body.add(box(6, 5.6, 5.6, coat, 0, 3, 9.5));
  body.add(box(3.4, 2.4, 2.6, CHARACTERS.CAT_PALE, 0, 1.6, 12)); // muzzle
  body.add(claySphere(0.9, CHARACTERS.DOG_NOSE, 0, 2.3, 13.2));
  body.add(claySphere(1, CHARACTERS.EYE, -1.7, 4.2, 11.6));
  body.add(claySphere(1, CHARACTERS.EYE, 1.7, 4.2, 11.6));

  // Ears: tall, and set **wide** on the skull. A cat's are at the corners of
  // its head where a fox's sit closer in — from above that spacing is most of
  // what says cat.
  const ears = [-2.5, 2.5].map((ex) => {
    const pivot = new THREE.Group();
    pivot.position.set(ex, 5.4, 9);
    pivot.add(box(2.5, 4, 1.6, dark, 0, 2, 0));
    pivot.add(box(1.2, 2.2, 0.9, CHARACTERS.CAT_PALE, 0, 1.7, 0.9));
    body.add(pivot);
    return pivot;
  });

  // The tail: thin, long, and carried nearly upright, with a hook at the top.
  // The hook is a second pill inside the same pivot, so it swings with the
  // tail rather than needing its own animation.
  const tail = pivotLimb(2.2, 11, coat, new THREE.Vector3(0, 3.2, -6.5));
  const hook = clayPill(1.1, 3, CHARACTERS.CAT_PALE);
  hook.position.set(0, -10.4, 1.4);
  hook.rotation.x = 1.1;
  tail.add(hook);
  tail.rotation.x = 2.75;
  body.add(tail);

  const paw = { color: CHARACTERS.CAT_PALE, width: 2.8, height: 1.8, depth: 3.2, forward: 0.6 };
  const legs = [
    pivotLimb(2.8, legH, dark, new THREE.Vector3(-2.4, legH, 4.4), paw),
    pivotLimb(2.8, legH, dark, new THREE.Vector3(2.4, legH, 4.4), paw),
    pivotLimb(2.8, legH, dark, new THREE.Vector3(-2.4, legH, -4.4), paw),
    pivotLimb(2.8, legH, dark, new THREE.Vector3(2.4, legH, -4.4), paw),
  ];

  return critterRig(body, legH + 3.4, legs, tail, ears);
}

/**
 * A red fox. Longer and lower than the cat, and mostly tail.
 *
 * **The brush does the work.** It's thick, nearly as long as the fox itself,
 * carried level behind rather than up, and it ends in a white tip. From
 * directly overhead that pale flag trailing a russet body is the most legible
 * thing on any critter in the game — you can identify a fox from the tail
 * alone, which is the point, because the fox is the one that costs you
 * something to touch.
 *
 * The rest is contrast against the cat: a **wedge** of a head with a long
 * pointed muzzle rather than a round skull, ears set close and dark-backed
 * rather than wide and cream, and **black stockings** rather than pale paws.
 * Two russet animals of similar size need every one of those to stay apart.
 */
function createFox(): CharacterRig {
  const coat = HAZARD.COLOR.fox;
  const legH = 5;
  const body = new THREE.Group();

  // Torso: longer and a touch deeper than the cat's, and it drops toward the
  // rump. A fox slinks; a cat stands.
  body.add(box(8.5, 7, 9, coat, 0, 0.4, 3.5));
  body.add(box(8, 6.6, 8, coat, 0, 0, -4));
  body.add(box(5, 3.6, 4, CHARACTERS.FOX_PALE, 0, -2.4, 7.5)); // white bib

  // A wedge of a head: skull, then a long muzzle tapering off it. The muzzle is
  // the cat's round face turned into a point, and it is doing as much work as
  // the tail from a shallower angle.
  body.add(box(6.6, 6, 6, coat, 0, 3.2, 10));
  body.add(box(3.4, 3, 5.5, coat, 0, 1.8, 14.5));
  body.add(box(3, 1.6, 4.5, CHARACTERS.FOX_PALE, 0, 0.6, 14.8)); // pale jaw
  body.add(claySphere(1.1, CHARACTERS.DOG_NOSE, 0, 2.2, 17));
  body.add(claySphere(1, CHARACTERS.EYE, -1.9, 4.4, 12.4));
  body.add(claySphere(1, CHARACTERS.EYE, 1.9, 4.4, 12.4));
  // Cheek ruffs, which is what stops the wedge reading as a beak.
  body.add(box(1.8, 3, 3, CHARACTERS.FOX_PALE, -3.2, 2.4, 11.4));
  body.add(box(1.8, 3, 3, CHARACTERS.FOX_PALE, 3.2, 2.4, 11.4));

  // Big ears, set close together and dark on the back.
  const ears = [-2.1, 2.1].map((ex) => {
    const pivot = new THREE.Group();
    pivot.position.set(ex, 5.8, 9.4);
    pivot.add(box(3, 5, 1.8, CHARACTERS.FOX_SOCK, 0, 2.5, 0));
    pivot.add(box(1.6, 3, 1, CHARACTERS.FOX_PALE, 0, 2.1, 1));
    body.add(pivot);
    return pivot;
  });

  // The brush, and its white tip. Carried level — `pivotLimb` hangs downward,
  // so ~1.6 rad lays it out behind rather than standing it up like the cat's.
  //
  // It overhangs `HAZARD.SIZE`, and that's deliberate: the collision circle is
  // the fox's *body*, so brushing the tail costs you nothing. Being tangled by
  // a fox you never touched would be the worse way to be wrong.
  const tail = pivotLimb(5.8, 13, coat, new THREE.Vector3(0, 3, -7.5));
  tail.add(claySphere(3.2, CHARACTERS.FOX_PALE, 0, -12.2, 0));
  tail.rotation.x = 1.42;
  body.add(tail);

  const sock = { color: CHARACTERS.FOX_SOCK, width: 3, height: 1.8, depth: 3.4, forward: 0.6 };
  const legs = [
    pivotLimb(3.2, legH, CHARACTERS.FOX_SOCK, new THREE.Vector3(-2.8, legH, 5), sock),
    pivotLimb(3.2, legH, CHARACTERS.FOX_SOCK, new THREE.Vector3(2.8, legH, 5), sock),
    pivotLimb(3.2, legH, CHARACTERS.FOX_SOCK, new THREE.Vector3(-2.8, legH, -5), sock),
    pivotLimb(3.2, legH, CHARACTERS.FOX_SOCK, new THREE.Vector3(2.8, legH, -5), sock),
  ];

  return critterRig(body, legH + 3.6, legs, tail, ears);
}

/**
 * A grey squirrel. The smallest of the three, and almost entirely tail.
 *
 * It was the last critter still built out of the generic lump. Its plume was
 * one fat sausage, which at least told it apart from the cat and the fox, but a
 * sausage is exactly what a squirrel's tail isn't.
 *
 * **The plume is now a stack of squashed balls arching up and hooking forward
 * over the back**, and it does three jobs at once. It is *bushy* rather than
 * rolled, built the way the tree canopies are — overlapping lumps, no two the
 * same. It is **broad**, which is a deliberate lie: a real squirrel's tail is
 * flat side to side, and built that way it reads from this camera as a narrow
 * lumpy stripe down the animal's back. Widened across, it reads as the fan
 * everyone pictures. And it is **rimmed cream**, which is the piece that
 * actually wins from overhead — a brown plume lying on a brown back is one
 * shape, and a pale edge is what cuts it out again.
 *
 * The rest is a small hunched body with the **rump carried higher than the
 * shoulders** — a squirrel is a coiled thing where a cat stands level and a fox
 * slinks — plus big dark eyes set round the sides of the skull, and small round
 * tufted ears rather than the cat's wide points or the fox's tall dark
 * triangles.
 */
function createSquirrel(): CharacterRig {
  const coat = HAZARD.COLOR.squirrel;
  const dark = HAZARD.DARK_COLOR.squirrel;
  const pale = CHARACTERS.SQUIRREL_PALE;
  const legH = 4.5;
  const body = new THREE.Group();

  // Small, compact and hunched: the rump sits higher and rounder than the
  // shoulders. That arch is the posture cue, and it survives from directly
  // above where none of the face does.
  body.add(box(7, 6.5, 7, coat, 0, -0.2, 3));
  body.add(box(7.6, 7.4, 6.5, coat, 0, 0.8, -3.2));
  body.add(box(4.6, 3.2, 5, pale, 0, -2.6, 4.2)); // cream front

  // A small round skull with a blunt muzzle — neither the cat's ball nor the
  // fox's wedge.
  body.add(box(5.4, 5, 4.8, coat, 0, 3.4, 7.6));
  body.add(box(3, 2.2, 2.4, pale, 0, 2.2, 9.8));
  body.add(claySphere(0.8, CHARACTERS.DOG_NOSE, 0, 2.8, 11));
  // Eyes big for the head and set well round the sides: a squirrel's are on the
  // corners of its skull, which is most of what makes it read as prey.
  body.add(claySphere(1.2, CHARACTERS.EYE, -2.2, 4.2, 9));
  body.add(claySphere(1.2, CHARACTERS.EYE, 2.2, 4.2, 9));

  // Ears: small, round, set close, with a cream tuft. The tuft does the
  // identifying — a bare round ear at this size is just a bump on the skull.
  const ears = [-1.8, 1.8].map((ex) => {
    const pivot = new THREE.Group();
    pivot.position.set(ex, 5.4, 7.2);
    pivot.add(claySphere(1.5, dark, 0, 1.2, 0));
    pivot.add(box(1.2, 2.2, 1, pale, 0, 2.6, -0.2));
    body.add(pivot);
    return pivot;
  });

  // The plume. Lumps stacked down the pivot's own axis — limbs here hang along
  // −y — drifting in +z as they climb, so the whole thing curls forward over
  // the back rather than standing up like a mast.
  const tail = new THREE.Group();
  tail.position.set(0, 4, -6);
  // [along the tail, forward drift, radius, frosted?]
  //
  // They overlap heavily on purpose. Spaced to just touch, the plume reads as a
  // string of beads — a caterpillar riding the squirrel — where sunk into each
  // other they merge into one bushy mass with a lumpy outline, which is the
  // whole point of building it from balls rather than rolling one.
  const plume: [number, number, number, boolean][] = [
    [-2.2, 0, 3.1, false],
    [-4.8, 0.4, 3.8, false],
    [-7.2, 1.3, 3.9, false],
    [-9.4, 2.6, 3.4, true],
    [-11.2, 4.2, 2.5, true],
  ];
  for (const [along, drift, r, frosted] of plume) {
    const lump = claySphere(r, frosted ? pale : coat, 0, along, drift);
    // Wide across, shallow through: a fan rather than a rope. See the note
    // above on why this goes against the anatomy.
    lump.scale.set(1.2, 1, 0.82);
    tail.add(lump);
  }
  tail.rotation.x = 2.42;
  body.add(tail);

  // Hind feet larger than the front, which is what a squirrel pushes off with.
  const front = { color: dark, width: 2.4, height: 1.6, depth: 2.6, forward: 0.4 };
  const hind = { color: dark, width: 3, height: 1.8, depth: 3.8, forward: 0.8 };
  const legs = [
    pivotLimb(2.6, legH, dark, new THREE.Vector3(-2.3, legH, 4), front),
    pivotLimb(2.6, legH, dark, new THREE.Vector3(2.3, legH, 4), front),
    pivotLimb(3, legH, dark, new THREE.Vector3(-2.6, legH, -3.6), hind),
    pivotLimb(3, legH, dark, new THREE.Vector3(2.6, legH, -3.6), hind),
  ];

  return critterRig(body, legH + 3.2, legs, tail, ears);
}

/**
 * A feral pigeon. The only bird, and the only critter built around its wings.
 *
 * Three of these animals are mammals with a tail as their signature. The pigeon
 * cannot use that trick — its tail is a short fan, and from overhead a fan is
 * indistinguishable from a rump. What it has instead is **a back divided into
 * three lengthways**: two pale barred wing panels folded down either flank with
 * a slate spine between them, and dark primaries crossing behind. Nothing else
 * in the game has a marking that runs fore-and-aft, and that is the whole
 * identification at a glance. It is also why the wings are separate meshes even
 * at rest — see the note where they are built.
 *
 * The other three decisions are all about not being a fourth brown lump:
 *
 * - **It is grey.** Slate against a cast of russets and browns, which is most
 *   of what the eye picks up before any shape resolves.
 * - **Two black bars across pale wing panels.** A pigeon's wing is lighter than
 *   its back with a pair of hard bars across it, and that stripe pattern is the
 *   most legible marking on the bird from directly above — the same argument
 *   the cat's tabby barring won, on a surface twice the size.
 * - **A green throat and a coral beak.** Two small warm/cool notes on the head
 *   end, which is what stops the front and the back being the same grey wedge
 *   at this distance.
 *
 * The wings go in `rig.wings` rather than `rig.arms`: arms are swung fore and
 * aft in antiphase by `animateWalk`, and a bird beats both wings together. See
 * `animateFlap`.
 */
function createPigeon(): CharacterRig {
  const coat = HAZARD.COLOR.pigeon;
  const dark = HAZARD.DARK_COLOR.pigeon;
  const pale = CHARACTERS.PIGEON_PALE;
  const legH = 3.4;
  const body = new THREE.Group();

  // A plump, blunt body that sits low and level. No shoulder/rump arch: a
  // pigeon on the pavement is a loaf, and that flatness is what the wings get
  // to stick out from.
  body.add(box(8, 6.5, 9, coat, 0, 0, 1));
  body.add(box(6.5, 5.5, 5, coat, 0, -0.4, -5)); // rear, tapering into the tail
  body.add(box(5, 3.4, 6, pale, 0, -2.2, 2)); // pale breast

  // The tail: a short wide fan carried level and low, with a dark band across
  // the end. Wide rather than long — that is what makes it read as a fan and
  // not as another mammal's tail.
  const tail = new THREE.Group();
  tail.position.set(0, 0.4, -7);
  const fan = box(7, 1.4, 7, coat, 0, 0, -3);
  tail.add(fan);
  tail.add(box(7.2, 1.5, 1.6, CHARACTERS.PIGEON_BAR, 0, 0.1, -6));
  body.add(tail);

  // A small round head on a short neck, with the oil-slick throat below it. The
  // sheen is deliberately only on the neck: a whole green head is a parrot.
  body.add(box(4.6, 4.4, 4.4, coat, 0, 3.4, 5.6));
  body.add(box(4.4, 2.6, 3.4, CHARACTERS.PIGEON_SHEEN, 0, 1.2, 5.2));
  body.add(box(1.5, 1.4, 2.8, CHARACTERS.PIGEON_CORAL, 0, 3.4, 8.4)); // beak
  body.add(claySphere(0.85, CHARACTERS.EYE, -1.9, 4.2, 6.6));
  body.add(claySphere(0.85, CHARACTERS.EYE, 1.9, 4.2, 6.6));

  // The wings, **folded**. Each is a pale barred panel lying along the flank
  // with its dark primaries trailing back past the rump, hung from a shoulder
  // pivot on top of the body so rotating the pivot lifts the whole wing — the
  // same trick `pivotLimb` plays with a leg, built by hand because a wing is a
  // flat plate rather than a rolled sausage.
  //
  // Folded is the whole point of the rest pose. Built spread — panels straight
  // out either side, which is the first thing anyone draws — the bird is an
  // **aeroplane** from this camera, and a grey aeroplane standing on a lawn is
  // not a pigeon. Along the back it is a bird with its wings closed, and
  // `animateFlap` is then the only thing that ever opens them.
  const wings = [-1, 1].map((side) => {
    const pivot = new THREE.Group();
    pivot.position.set(side * 2.6, 2.6, 0.5);
    pivot.add(box(3.6, 1.8, 9, pale, side * 1.9, -0.3, -0.5));
    // Two bars across the panel — the pigeon's most legible marking from
    // overhead, and the reason the panel is pale rather than slate.
    pivot.add(box(3.7, 1.9, 1.3, CHARACTERS.PIGEON_BAR, side * 1.9, -0.25, 1.6));
    pivot.add(box(3.7, 1.9, 1.1, CHARACTERS.PIGEON_BAR, side * 1.9, -0.25, -0.6));
    // Primaries: the dark point of a closed wing, crossed over the tail.
    pivot.add(box(2.4, 1.6, 4.4, dark, side * 1.9, -0.3, -6));
    body.add(pivot);
    return pivot;
  });

  // Coral feet, short and set well back — a pigeon's legs are under its middle,
  // not at the corners like a four-legged critter's.
  const foot = {
    color: CHARACTERS.PIGEON_CORAL,
    width: 2.2,
    height: 1.2,
    depth: 3.4,
    forward: 0.9,
  };
  const legs = [
    pivotLimb(1.8, legH, CHARACTERS.PIGEON_CORAL, new THREE.Vector3(-1.9, legH, 1.2), foot),
    pivotLimb(1.8, legH, CHARACTERS.PIGEON_CORAL, new THREE.Vector3(1.9, legH, 1.2), foot),
  ];

  const rig = critterRig(body, legH + 3.2, legs, tail, []);
  rig.wings = wings;
  return rig;
}

// ---- road vehicles -----------------------------------------------------------

/**
 * A car or a van. Front is +z, like everything else in this file.
 *
 * A car used to be a body-coloured tub with one dark block on top, four
 * flattened balls and a pair of headlights on the nose — the least-built thing
 * left in a town of coursed brick and dressed roofs. Rebuilt around one
 * observation:
 *
 * **From this camera you are looking at the roof.** At 58° of tilt a vehicle
 * presents its plan view and a sliver of one flank; the nose, where the old
 * model kept its only ornament, is almost never seen. So the detail is spent
 * top-down, and that is also what separates the two kinds:
 *
 * - **The roof stays painted**, and the glass is a band *around* it. On a car
 *   that band goes all the way round — windscreen, rear window and both sides.
 *   On a van it stops at the cab, and the rest is a long painted box. That's
 *   the whole difference in plan, and it's enough: you can tell them apart
 *   from directly above without seeing a single vertical face.
 * - A dark cabin reads as a *hole cut in the roof*, which is exactly what the
 *   old car looked like from overhead. Hence the painted cap on both kinds.
 * - **Lamps sit on the top edge**, not on the vertical faces, for the same
 *   reason the bin's mouth had to go on the crown of its lid.
 *
 * Everything below the deck is shared, because a van's chassis is a car's: the
 * hull, the sill rail, the wheels, the bumpers, the lamps and the mirrors are
 * built once here and the two `body` functions only differ above the bonnet.
 *
 * Heights come from `VEHICLES.KINDS[kind]`, and each kind's roof lands exactly
 * on its declared `HEIGHT` — `models.test.ts` holds that for both.
 */
export function createVehicle(kind: VehicleKind, tint: number): THREE.Group {
  const group = new THREE.Group();
  const { WIDTH: wid } = VEHICLES;
  const { LENGTH: len, HEIGHT: hgt, DECK: deck } = VEHICLES.KINDS[kind];
  const nose = len / 2;
  const half = wid / 2;

  // Wheels are sized off the *width*, not the height: a van rides on the same
  // wheels as a car, and scaling them with the body would put cartwheels under
  // anything tall.
  const wheelR = wid * 0.23;

  // The hull: bonnet, boot and door bottoms in one block, its top face the deck
  // everything else is measured from.
  group.add(box(wid, deck - wheelR, len, tint, 0, (deck + wheelR) / 2, 0));

  // A darker rail along the sills, running between the wheels. It gives the
  // flank a line at door-bottom height, and it is what stops the body reading
  // as a single slab of colour from the side.
  group.add(
    box(wid * 0.98, 3.4, len * 0.86, shadeHex(tint, PROPS.CAR_SILL_DARKEN), 0, wheelR + 2, 0),
  );

  if (kind === 'van') addVanBody(group, tint, len, wid, hgt, deck);
  else addCarBody(group, tint, len, wid, hgt, deck);

  // Wheels along both flanks — squashed clay balls rather than cylinders, which
  // are the one shape you can't roll by hand.
  //
  // They stand a little proud of `VEHICLES.WIDTH`, and so of the footprint
  // `bounds()` hit-tests. That's the safe direction to be wrong in: you can clip
  // a wheel and live, rather than being run over by something that hasn't
  // touched you.
  //
  // They get no hubs. A pale hub was built and thrown away: it sits on the
  // outboard face of the wheel, which is the one plane this camera never turns
  // toward you, so it disappeared exactly the way the old headlights did.
  for (const wx of [-1, 1]) {
    for (const wz of [1, -1]) {
      const wheel = claySphere(wheelR, PROPS.CAR_TYRE, wx * (half + 0.9), wheelR, wz * len * 0.3);
      wheel.scale.x = 0.4; // flatten across the axle
      group.add(wheel);
    }
  }

  // Bumpers, standing proud of each end so they read as a band across the nose
  // and tail from above, and a grille under the front one.
  for (const bz of [1, -1]) {
    group.add(box(wid * 0.98, 4, 3.6, PROPS.CAR_TRIM, 0, deck * 0.66, bz * (nose - 0.6)));
  }
  group.add(box(wid * 0.52, 2.6, 1.4, PROPS.CAR_TRIM, 0, deck - 1.6, nose - 0.4));

  // Headlamps, on the top edge of the nose where this camera can see them. The
  // tail is the body functions' problem — where a rear lamp can go depends on
  // what is standing behind it, and on a van the answer is "nowhere sensible".
  for (const lx of [-1, 1]) {
    const lamp = claySphere(2.4, PROPS.CAR_LAMP, lx * wid * 0.29, deck - 0.6, nose - 2.2);
    lamp.material = LAMP_MATERIAL;
    lamp.scale.set(1, 0.65, 0.55);
    group.add(lamp);
  }

  // Wing mirrors. Tiny, and the single clearest "this end is the front" cue in
  // the plan view.
  for (const mx of [-1, 1]) {
    group.add(
      box(3, 1.4, 2, PROPS.CAR_TRIM, mx * (half + 1), deck + 0.4, cabFrontZ(kind, len) - 0.6),
    );
  }

  // Blinkers (indicators), mounted at the four corners of the vehicle.
  //
  // Which flank counts as "left" is not a free choice, and it is not the sign
  // that reads as left on the bench. The model is built nose-down local +z, and
  // `syncMesh` yaws it by `atan2(vx, vy)`, which swings local +x round to
  // gameplay (vy, -vx): due north for an eastbound car, and north is that
  // driver's left. So local **+x is the left flank** — the mirror of what the
  // -1-first loop order suggests. Pair them the other way and every vehicle in
  // the level indicates one way and turns the other.
  const leftBlinkers: THREE.Mesh[] = [];
  const rightBlinkers: THREE.Mesh[] = [];
  for (const lx of [-1, 1]) {
    const fBlinker = claySphere(1.8, PROPS.CAR_BLINKER, lx * (half - 1.0), deck - 0.4, nose - 1.5);
    fBlinker.material = BLINKER_MATERIAL;
    fBlinker.scale.set(0.8, 0.7, 0.7);
    fBlinker.visible = false;
    group.add(fBlinker);

    const rBlinker = claySphere(1.8, PROPS.CAR_BLINKER, lx * (half - 1.0), deck - 0.4, -nose + 1.5);
    rBlinker.material = BLINKER_MATERIAL;
    rBlinker.scale.set(0.8, 0.7, 0.7);
    rBlinker.visible = false;
    group.add(rBlinker);

    if (lx === 1) leftBlinkers.push(fBlinker, rBlinker);
    else rightBlinkers.push(fBlinker, rBlinker);
  }
  group.userData = { ...group.userData, leftBlinkers, rightBlinkers };

  return group;
}

/** Shared so every lamp on every vehicle is one material. */
const LAMP_MATERIAL = clayMaterial(PROPS.CAR_LAMP, {
  emissive: PROPS.CAR_LAMP,
  emissiveIntensity: 0.8,
});

const BLINKER_MATERIAL = clayMaterial(PROPS.CAR_BLINKER, {
  emissive: PROPS.CAR_BLINKER,
  emissiveIntensity: 1.0,
});

/**
 * How much bare deck a kind leaves in front of its windscreen. A van's is short
 * — that snub nose is half of what makes it a van — but it can't be *nothing*:
 * the headlamps sit on that deck, and a cab that runs all the way to the bumper
 * swallows them whole. Which is exactly how the van shipped the first time.
 */
const BONNET = 5.6;

/** Where the windscreen sits along z — the mirrors hang either side of it. */
function cabFrontZ(kind: VehicleKind, len: number): number {
  return kind === 'van' ? len / 2 - BONNET : len * 0.21;
}

/**
 * A car above the bonnet: one dark glass block set back on the deck, with the
 * painted roof laid on top and inset on every side, so a band of glass shows
 * all round it.
 *
 * Separate raked screens were tried first and are worse — at this size they
 * read as two black bars stuck to the roof.
 */
function addCarBody(
  group: THREE.Group,
  tint: number,
  len: number,
  wid: number,
  hgt: number,
  deck: number,
): void {
  const cabinLen = len * 0.42;
  const cabinZ = -1.5;
  const roofThick = 1.1;

  group.add(
    box(wid * 0.84, hgt - deck + 1.4, cabinLen, PROPS.CAR_GLASS, 0, (hgt + deck - 1.4) / 2, cabinZ),
  );
  // The roof is pushed back, so the windscreen band is deeper than the rear
  // one. Along with the mirrors, that asymmetry is the only thing giving a car
  // a front when you're looking straight down on it.
  group.add(
    box(
      wid * 0.72,
      roofThick,
      cabinLen * 0.7,
      shadeHex(tint, PROPS.CAR_ROOF_LIGHTEN),
      0,
      hgt - roofThick / 2, // the roof *is* the declared height
      cabinZ - 1.2,
    ),
  );

  // Tail lamps on the top edge of the boot. A car's roof is well forward of its
  // tail, so nothing is standing over them — which is not true of a van.
  for (const lx of [-1, 1]) {
    group.add(box(4.6, 2.2, 1.6, PROPS.CAR_TAIL_LAMP, lx * wid * 0.3, deck - 0.8, -len / 2 + 1.8));
  }
}

/**
 * A van above the bonnet: a short glazed cab at the front and a painted box
 * running from behind it to the tail, standing a step taller than the cab.
 *
 * The box is the point. It gives the plan view a long uninterrupted panel of
 * paint where a car has a small one ringed with glass, and the step down to the
 * cab draws a line across the roof exactly where the glass starts. Between them
 * that is enough to tell the two apart from directly overhead, which is the
 * only view you reliably get.
 *
 * **The tail lamps go on the roof, and this is not a stylistic choice.** A van's
 * box is full height right to the back, so anything mounted on the rear face is
 * behind its own roof from a camera 58° above and in front: trace the sightline
 * from a lamp 3 units below the roof line and it passes straight through the
 * roof slab. There is no height on that face that works. Roof-edge marker lamps
 * are what commercial vehicles actually carry, and they're the only red this
 * camera can see — the same problem the headlights had, one step further on.
 */
function addVanBody(
  group: THREE.Group,
  tint: number,
  len: number,
  wid: number,
  hgt: number,
  deck: number,
): void {
  const tail = -len / 2;
  const cabLen = len * 0.2;
  const cabZ = cabFrontZ('van', len) - cabLen / 2;
  const cabTop = hgt - 2.2; // the step down from the box roof
  const roofThick = 1.1;

  // The glazed cab. Its own roof is the top of the glass — a van's cab roof is
  // narrow enough that painting it would just read as a lid.
  group.add(
    box(
      wid * 0.86,
      cabTop - deck + 1.4,
      cabLen,
      PROPS.CAR_GLASS,
      0,
      (cabTop + deck - 1.4) / 2,
      cabZ,
    ),
  );

  // The box body, from behind the cab to the tail.
  const boxLen = cabZ - cabLen / 2 - tail - 0.6;
  const boxZ = cabZ - cabLen / 2 - boxLen / 2;
  group.add(box(wid * 0.94, hgt - deck + 1.4, boxLen, tint, 0, (hgt + deck - 1.4) / 2, boxZ));
  // A lighter panel let into the roof, the same lift a car's roof gets. On a
  // surface this large it is doing more work: an unbroken painted rectangle
  // 40 units long is the biggest flat area on any moving thing in the town.
  group.add(
    box(
      wid * 0.76,
      roofThick,
      boxLen * 0.82,
      shadeHex(tint, PROPS.CAR_ROOF_LIGHTEN),
      0,
      hgt - roofThick / 2,
      boxZ,
    ),
  );

  // Marker lamps on the trailing edge of the roof — see the note above. Flush
  // with it rather than proud: the roof is the declared HEIGHT and nothing on a
  // vehicle is allowed above that.
  for (const lx of [-1, 1]) {
    group.add(box(3.4, 1.6, 2.4, PROPS.CAR_TAIL_LAMP, lx * wid * 0.28, hgt - 0.8, tail + 2.4));
  }
}

// ---- procedural animation ----------------------------------------------------

/**
 * Drive a walk cycle. `phase` advances with distance travelled (see ANIM), so the
 * legs churn faster the faster the entity moves, and freeze when it stops.
 *
 * Limbs alternate: even-indexed limbs swing forward while odd-indexed swing back.
 * For the dog's four legs that produces a natural diagonal trot.
 */
export function animateWalk(
  rig: CharacterRig,
  phase: number,
  moving: boolean,
  legSwing: number,
  bob: number,
): void {
  const swing = moving ? Math.sin(phase) : 0;

  rig.legs.forEach((leg, i) => {
    // Dog's back pair (indices 2,3) is offset half a cycle from the front pair.
    const flip = i % 2 === 0 ? 1 : -1;
    const diagonal = i >= 2 ? -1 : 1;
    leg.rotation.x = swing * legSwing * flip * diagonal;
  });

  rig.arms.forEach((arm, i) => {
    // Arms counter-swing against the legs.
    arm.rotation.x = -swing * ANIM.PLAYER_ARM_SWING * (i % 2 === 0 ? 1 : -1);
  });

  if (rig.body) {
    // Two bobs per stride (body rises on each footfall), hence cos(2·phase).
    rig.body.position.y = rig.bodyBaseY + (moving ? Math.abs(Math.cos(phase)) * bob : 0);
  }
}

/** Wag a tail. `t` is elapsed seconds; `speed` scales the wag rate. */
export function animateTail(rig: CharacterRig, t: number, speed = 1, amount = 1): void {
  if (!rig.tail) return;
  if (speed <= 0) {
    rig.tail.rotation.z = 0;
    return;
  }
  rig.tail.rotation.z = Math.sin(t * ANIM.DOG_TAIL_HZ * speed) * ANIM.DOG_TAIL_WAG * amount;
}

/**
 * Apply a mood pose on top of the walk cycle: forward lean, ear angle, and how
 * far the body drops toward the ground. Call *after* `animateWalk`, which owns the
 * body's base height — the crouch is subtracted from whatever it set.
 */
export function applyPose(rig: CharacterRig, pose: DogPose): void {
  if (rig.body) {
    rig.body.rotation.x = pose.lean;
    rig.body.position.y -= pose.crouch;
  }
  // Ears are modelled pointing up, so a positive perk rotates them back/upright
  // and a negative one folds them down against the head.
  for (const ear of rig.ears) ear.rotation.x = -pose.earPerk;
}

/**
 * Hopping gait for the small critters — they bound rather than glide. The body
 * rises in an arc each hop and squashes on landing, which reads far livelier than
 * sliding a mesh around.
 */
export function animateHop(rig: CharacterRig, phase: number, moving: boolean): void {
  if (!rig.body) return;
  if (!moving) {
    rig.body.position.y = rig.bodyBaseY;
    rig.body.scale.set(1, 1, 1);
    return;
  }
  // One hop per half-cycle of the walk phase.
  const hopT = (phase / Math.PI) % 1;
  const height = arc01(hopT) * ANIM.HAZARD_HOP_HEIGHT;
  rig.body.position.y = rig.bodyBaseY + height;

  // Squash near the ground, stretch at the apex — classic anticipation/follow-through.
  const squash = 1 - (1 - arc01(hopT)) * ANIM.HAZARD_SQUASH;
  rig.body.scale.set(2 - squash, squash, 2 - squash);

  // Legs tuck up while airborne.
  for (const leg of rig.legs) leg.rotation.x = height * 0.03;
}

/**
 * Beat a bird's wings. `t` is elapsed seconds; `flying` is whether it is
 * actually in the air.
 *
 * Both wings move **together**, rolled up about the body's long axis, which is
 * the whole difference between a bird and the antiphase arm swing `animateWalk`
 * gives a person. On the ground the beat collapses to a slow shuffle at a
 * fraction of the amplitude — a pigeon on the pavement fidgets with its wings
 * rather than holding them like a display model, but it is emphatically not
 * flying, and the two must not be confusable at a glance.
 */
export function animateFlap(rig: CharacterRig, t: number, flying: boolean): void {
  if (!rig.wings) return;
  const hz = flying ? ANIM.WING_FLAP_HZ : ANIM.WING_FLAP_HZ / 6;
  const amount = flying ? ANIM.WING_FLAP : ANIM.WING_FOLD;
  // In the air the beat is added on top of a held spread, so the bottom of a
  // stroke is still a bird with its wings out. On the ground there is no
  // spread at all and the sweep starts from folded, which is where a walking
  // pigeon keeps them.
  const base = flying ? ANIM.WING_SPREAD : 0;
  const lift = base + (Math.sin(t * hz * Math.PI * 2) * 0.5 + 0.5) * amount;
  rig.wings.forEach((wing, i) => {
    // Mirrored: wing 0 reaches out along −x and wing 1 along +x, so opposite
    // rolls are what puts *both* tips up rather than tipping the bird over.
    wing.rotation.z = i === 0 ? -lift : lift;
  });
}

/** Startle: the critter flattens its ears and freezes low. `t` is 0..1 intensity. */
export function applyStartle(rig: CharacterRig, t: number): void {
  for (const ear of rig.ears) ear.rotation.x = -t * 1.2;
  if (rig.body) rig.body.position.y -= t * 2;
}

// ─────────────────────────────────────────────────────────────────────────────
// Landmark props & the bone doormat
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The furniture on the greens: a fountain, a bench, a phone kiosk and a pillar
 * box. Where each one stands is decided by the pure `systems/parkFurniture.ts`;
 * this is what they're made of.
 *
 * All four were first built as what the object looks like *from the side* — a
 * red box with a ball on it, a red sausage, a stone blob with a square of water
 * through it — and from this camera none of them was identifiable. They're
 * rebuilt here against the two rules in `AGENTS.md` that a prop this small has
 * to obey:
 *
 * - **Whatever identifies the object goes on top.** The camera is 58° above the
 *   horizon, so what you get of a waist-high prop is its crown and a sliver of
 *   the flank nearest you (+z, the south face — the camera sits on the +z side).
 *   The kiosk's crown emblem, the postbox's brass ring, the fountain's
 *   concentric rings of stone and water are all there so the thing reads from
 *   directly overhead. The kiosk's sign band and the postbox's slot are on the
 *   +z face for the same reason, and the placer never turns them away from it.
 * - **Everything visible needs a tonal break.** A single-colour rounded prop
 *   melts into one lump from up here exactly like a single-colour animal does.
 *
 * And one rule of arithmetic: **nothing may reach below y = 0.** These sit on
 * the turf, so a shape whose bounding box dips under the origin is a prop
 * half-buried in the grass — which is what a capsule used as a basin does.
 */

/**
 * The three shapes the props need that the clay kit doesn't hand out: a pressed
 * dome, a coil laid flat, and a round slab. Built here from raw geometry the way
 * the bin's lid and foot are, and given a clay material so they belong.
 */
const ROUND_SEGMENTS = CLAY.SPHERE_SEGMENTS + 6;

/** A shallow clay cap — a dome that has been pressed down rather than a ball. */
function dome(r: number, rise: number, color: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.SphereGeometry(
      r,
      ROUND_SEGMENTS,
      ROUND_SEGMENTS / 2,
      0,
      Math.PI * 2,
      0,
      Math.PI / 2,
    ),
    clayMaterial(color),
  );
  mesh.scale.y = rise / r;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** A coil of clay laid flat — the rolled rim of a basin, a band round a cap. */
function ring(major: number, tube: number, color: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.TorusGeometry(major, tube, 8, ROUND_SEGMENTS),
    clayMaterial(color),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** A round slab: the water inside a basin, a plinth under a pillar box. */
function disc(rTop: number, rBottom: number, h: number, color: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(rTop, rBottom, h, ROUND_SEGMENTS),
    clayMaterial(color),
  );
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * How high the bone lies. Its knuckles are the widest thing on the mat, so this
 * is what keeps them pressed into the coir rather than sunk through it and out
 * the underside of the doorstep.
 */
const BONE_Y = 2.4;

/** A Play-doh doormat with a bone motif, for the walker's own front step. */
export function createDoormatWithBone(): THREE.Group {
  const group = new THREE.Group();

  // Coir mat, with a lighter panel inside its border. Left as one flat brown
  // slab it reads as a shadow on the doorstep rather than as a mat.
  const mat = box(24, 1.4, 14, PROPS.DOORMAT);
  mat.position.y = 0.7;
  group.add(mat);

  const panel = box(19, 1.5, 9.5, shadeHex(PROPS.DOORMAT, 0.16));
  panel.position.y = 0.85;
  group.add(panel);

  // The bone: a rolled shaft with a pair of knuckles sunk into each end. Sunk
  // in rather than set beside — spaced to just touch, the balls read as a
  // string of beads instead of as one bone (the same trap as a bushy tail).
  const shaft = clayPill(1.5, 6.5, PROPS.DOORMAT_BONE);
  shaft.rotation.z = Math.PI / 2;
  shaft.position.y = BONE_Y;
  group.add(shaft);

  for (const ox of [-4.4, 4.4]) {
    for (const oz of [-1.6, 1.6]) {
      const knuckle = claySphere(2, PROPS.DOORMAT_BONE);
      knuckle.position.set(ox, BONE_Y, oz);
      group.add(knuckle);
    }
  }

  return group;
}

/**
 * A red telephone kiosk.
 *
 * What makes a K6 a K6 from above is not the red — the pillar box is red too —
 * but the **glazing**: a grid of pale panes held in a red frame, under a domed
 * cap. So the shell is built as four corner posts with a glazed core standing
 * back between them and two rails round it, and the crown emblem that lives on
 * the fascia in real life is moved onto the crown of the dome, where the camera
 * can actually see it. Same move as the bin's mouth.
 *
 * Kept just under the walker's height: it is the tallest thing that stands on a
 * green, and anything tall is drawn 0.63 × its height north on screen.
 */
export function createPhoneBox(): THREE.Group {
  const group = new THREE.Group();
  const W = 18; // footprint
  const POST = 3.6;
  const H = 40; // posts, to the underside of the fascia
  const red = PROPS.PHONE_BOX;

  // Glazed core, standing back from the posts so the frame reads as a frame.
  const glass = box(W - 3, 33, W - 3, PROPS.PHONE_BOX_GLASS);
  glass.position.y = 22;
  group.add(glass);

  // Solid lower panel, and two rails across the glass — the glazing bars.
  const kick = box(W, 5, W, red);
  kick.position.y = 2.5;
  group.add(kick);
  for (const y of [14, 26]) {
    const rail = box(W, 1.8, W, shadeHex(red, -0.08));
    rail.position.y = y;
    group.add(rail);
  }

  for (const ox of [-1, 1]) {
    for (const oz of [-1, 1]) {
      const post = box(POST, H, POST, red);
      post.position.set((ox * (W - POST)) / 2, H / 2, (oz * (W - POST)) / 2);
      group.add(post);
    }
  }

  // Fascia, oversailing the posts so it throws a shadow line right round.
  const fascia = box(W + 1.6, 5, W + 1.6, red);
  fascia.position.y = 41;
  group.add(fascia);

  // TELEPHONE sign, on all four faces as it is in life — which also means the
  // camera-facing one is never the blank side.
  for (const [ax, az] of [
    [0, 1],
    [0, -1],
    [1, 0],
    [-1, 0],
  ]) {
    const sign = box(az === 0 ? 0.7 : 11, 2.4, az === 0 ? 11 : 0.7, PROPS.PHONE_BOX_GLASS);
    sign.position.set(ax * (W / 2 + 0.9), 41, az * (W / 2 + 0.9));
    group.add(sign);
  }

  const cap = dome(9.2, 5.6, red);
  cap.position.y = 43.4;
  group.add(cap);

  // The crown, flat on the roof: the one detail that says "kiosk" when the
  // camera is looking straight down at it.
  const crown = disc(2.6, 3.2, 1.2, PROPS.POSTBOX_GOLD);
  crown.position.y = 48.6;
  group.add(crown);

  return group;
}

/**
 * A pillar box.
 *
 * The hard part is that from overhead it is a red circle with a domed top —
 * which is also a description of the litter bin, the one prop in the game the
 * player must never mistake for anything else. Three things separate them: the
 * colour, the brass ring under the cap (the bin has pressed ribs down its
 * body), and a mouth that is emphatically on the *side* rather than in the
 * crown of the lid.
 */
export function createPostBox(): THREE.Group {
  const group = new THREE.Group();
  const R = 7;
  const red = PROPS.POSTBOX;

  // A dark foot, so the rounded bottom of the body is buried rather than
  // reading as a sausage balanced on the grass.
  const foot = disc(R + 1.2, R + 1.9, 3, shadeHex(red, -0.62));
  foot.position.y = 1.5;
  group.add(foot);

  const body = clayPill(R, 17, red);
  body.position.y = 16;
  group.add(body);

  const brass = ring(R + 0.8, 0.9, PROPS.POSTBOX_GOLD);
  brass.position.y = 26;
  group.add(brass);

  const cap = dome(R + 0.9, 4.9, red);
  cap.position.y = 26.8;
  group.add(cap);

  // The slot, with a hood over it. The hood is what makes the opening read from
  // above: it catches the light and lays a hard line across the flank, where an
  // unshaded recess on a curved red surface disappears.
  const hood = box(11, 1.6, 2.6, shadeHex(red, -0.3));
  hood.position.set(0, 24.6, 5.6);
  group.add(hood);

  const slot = box(9.5, 2.2, 1.4, PROPS.POSTBOX_SLOT);
  slot.position.set(0, 22.9, 6.4);
  group.add(slot);

  const cypher = box(4.6, 3.4, 0.8, PROPS.POSTBOX_GOLD);
  cypher.position.set(0, 17, 6.8);
  group.add(cypher);

  return group;
}

/**
 * The fountain on the green.
 *
 * Built as **concentric rings**, because that is the whole of what a fountain
 * looks like from directly overhead: a stone step, a rolled stone rim, a disc
 * of water inside it, a pedestal, a smaller bowl, and the jet breaking white
 * over the middle. The first version put a *square* slab of water across a
 * round basin, which is the detail that made it unreadable — nothing else in
 * the town has a straight edge crossing a curved one.
 *
 * Low and wide on purpose: it stands in the middle of a green with the walk
 * going round it, and it is the one piece here that must not hide anybody.
 */
export function createParkFountain(): THREE.Group {
  const group = new THREE.Group();
  const stone = PROPS.FOUNTAIN_STONE;
  const water = PROPS.FOUNTAIN_WATER;

  // The step is only just wider than the rim, and darker than it. Built out to
  // a generous pale apron it reads as a dinner plate on the grass — there is
  // more stone in a fountain than water, and the water is the whole point.
  const step = disc(16.2, 17.4, 2.6, shadeHex(stone, -0.22));
  step.position.y = 1.3;
  group.add(step);

  const rim = ring(14.6, 2.6, stone);
  rim.position.y = 4.2;
  group.add(rim);

  // Reaches under the rim's inner curve, so the water is held by the basin
  // rather than laid over it.
  const pool = disc(13.6, 13.6, 1, water);
  pool.position.y = 4.6;
  group.add(pool);

  const pedestal = disc(3.2, 4.2, 7, stone);
  pedestal.position.y = 6.5;
  group.add(pedestal);

  const bowl = ring(5.2, 1.4, stone);
  bowl.position.y = 11;
  group.add(bowl);

  const upper = disc(4.6, 4.6, 0.7, water);
  upper.position.y = 11.2;
  group.add(upper);

  // The jet: three balls up the middle, whitening as they break.
  const jet: [number, number, number][] = [
    [2.1, 13.2, water],
    [1.6, 15.4, water],
    [1.2, 17.2, PROPS.FOUNTAIN_FOAM],
  ];
  for (const [r, y, color] of jet) {
    const ball = claySphere(r, color);
    ball.position.y = y;
    group.add(ball);
  }

  // …and the falling water, as four droplets on the way back down to the pool.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const drop = claySphere(1, PROPS.FOUNTAIN_FOAM);
    drop.position.set(Math.cos(a) * 8, 8.6, Math.sin(a) * 8);
    group.add(drop);
  }

  return group;
}

/**
 * A park bench.
 *
 * **Slats, and the gaps between them, are the whole model.** A bench is a low
 * flat thing, so almost all of what the camera gets is the seat and the back
 * lying in the plan — and as two solid boards that is a brown rectangle on the
 * grass. Three slats each, in two tones of wood between cast-iron ends, is what
 * makes it furniture.
 *
 * The back is on the −z side so the camera looks into the bench rather than at
 * the back of it, and the placer never turns it round.
 */
export function createParkBench(): THREE.Group {
  const group = new THREE.Group();
  const wood = PROPS.BENCH_WOOD;
  const iron = PROPS.BENCH_IRON;
  const SEAT_Y = 11;

  for (const side of [-15, 15]) {
    const end = box(2.4, SEAT_Y, 11, iron);
    end.position.set(side, SEAT_Y / 2, 0);
    group.add(end);

    const arm = box(2.8, 1.8, 12.5, iron);
    arm.position.set(side, SEAT_Y + 1.4, -0.6);
    group.add(arm);
  }

  for (const oz of [-3.4, 0, 3.4]) {
    const slat = box(28, 1.5, 3, wood);
    slat.position.set(0, SEAT_Y + 0.4, oz);
    group.add(slat);
  }

  // The back leans away from the seat, which is also what stops its slats
  // stacking directly over the seat's from this angle.
  const back = new THREE.Group();
  back.position.set(0, SEAT_Y, -5.2);
  back.rotation.x = -0.16;
  group.add(back);

  for (const side of [-13.6, 13.6]) {
    const post = box(2.4, 12, 1.6, iron);
    post.position.set(side, 6, 0);
    back.add(post);
  }

  for (const y of [2.6, 6.5, 10.4]) {
    const slat = box(28, 2.8, 1.4, shadeHex(wood, 0.14));
    slat.position.set(0, y, 0);
    back.add(slat);
  }

  return group;
}


/**
 * A flower bed for the corner of a green.
 *
 * The one piece of park furniture that is not an object but a *patch*, and the
 * reason it exists is the far half of a green. Everything else here is tall, so
 * everything else has to stand on the south edge (`AGENTS.md` → the camera
 * tilt), which left the north half of every park bare mown nothing. A bed is
 * two units high, so it can go where a bench cannot.
 *
 * A ring of soil with blooms heaped on it, rather than a disc of colour: from
 * overhead a flat coloured circle on grass reads as a stain, and the thing that
 * says "planted" is seeing the individual heads sitting proud of the earth. The
 * blooms take `FOLIAGE.BLOOMS` — the same hot colours as the window boxes, and
 * they break the palette's cool-town rule for the same reason those do: each
 * head is two units across.
 */
export function createFlowerBed(): THREE.Group {
  const group = new THREE.Group();
  const r = PARK_FURNITURE.BED_RADIUS;

  // The soil, dished: wider at the base than the top so the bed reads as banked
  // earth rather than as a tin lid dropped on the grass.
  const soil = disc(r * 0.92, r, 2.2, YARDS.SOIL_COLOR);
  soil.position.y = 1.1;
  group.add(soil);

  // Leaves first, as a low mound, so the heads have something to sit in.
  for (let i = 0; i < PARK_FURNITURE.BED_BLOOMS; i++) {
    const a = (i / PARK_FURNITURE.BED_BLOOMS) * Math.PI * 2;
    const reach = r * 0.52;
    const leaf = claySphere(3.1, FOLIAGE.BOX_LEAF, Math.cos(a) * reach, 2.6, Math.sin(a) * reach);
    leaf.scale.y = 0.6;
    group.add(leaf);

    // …and one head above each, alternating colour round the bed.
    const bloom = claySphere(
      2.1,
      FOLIAGE.BLOOMS[i % FOLIAGE.BLOOMS.length],
      Math.cos(a) * reach,
      4.4,
      Math.sin(a) * reach,
    );
    bloom.scale.y = 0.8;
    group.add(bloom);
  }

  // One in the middle, taller, so the bed has a crown instead of a hole.
  const centre = claySphere(2.6, FOLIAGE.BLOOMS[0], 0, 5, 0);
  centre.scale.y = 0.85;
  group.add(centre);

  return group;
}
